// Pipes (owner, 2026-10-06): draw pipe runs from fitting ends and between centre points, with
// typed lengths; pipes stay attached to fitting ends and are rebuilt when an end moves; a pipe
// end can be cut flat or shown as a circle (a floor penetration).
//
// THIS FILE IS THE CONTRACT between the pipe engine (owner: engine) and the tools/screens
// (owner: UI). Types and signatures are fixed. Bodies marked NOT_BUILT are filled in by the
// engine work; the UI work only imports from here.
//
// ---- Data model ----------------------------------------------------------------------------
// FITTING: a group with meta.fitting = { kind, size, size2? } (see src/library/fittings.ts) and
//   meta.ports = FittingPortData[] — absolute drawing mm, carried by every move/turn/scale/copy
//   like meta.pivot. Port order: bend [inlet, outlet]; tee [run inlet (left), run outlet (right),
//   branch]; reducer [large end, small end]. `dir` points OUT of the fitting along the pipe.
// PIPE: a group with meta.pipe = PipeMeta and five or seven polyline children, in this order:
//   [side 1, side 2, centre line (dash-dot), end A, end B, collar A, collar B]. The collars (3.15.2) are a circle
//   round a floor penetration end, a collar thickness larger than the pipe circle; each is hidden unless its end is
//   "circle". A pipe from before 3.15.2 has five children and gets its collars when the drawing opens
//   (addPipeCollarsInPlace, called by the load repair). The children's points are DERIVED
//   from meta.pipe and rebuilt by rebuildPatches(); never edit them directly.
//   A pipe has two ports: index 0 = end A (dir points from B towards A, i.e. out of the pipe),
//   index 1 = end B (dir from A towards B). Pipes can therefore attach to pipes (chains).
// End styles: "none" = joined (the end child is hidden), "flat" = a line across the end,
//   "circle" = a circle of the pipe's size centred on the end (pipe goes through the floor),
//   "flex" (3.15.6) = a rubber joint at the end: two PN16 flanges with a rounded rubber sphere between them, the face to
//   face length of a single sphere joint (library/flanges.ts). The pipe's sides start at the far flange. A pipe end
//   joined to the side of a pump gets it (owner, 2026-10-20: "when connecting pipes to the pumps, they do need to have
//   a collar and ... a rubber coupler to prevent noise ... an expanded rounded type pipe to show the expansion").
import type { Vec } from "../core/geometry";
import type { Doc, Entity } from "../doc/entities";
import type { Command } from "../doc/commands";
import type { GroupEntity, PolylineEntity, Style } from "../doc/entities";
import { normalize } from "../core/geometry";
import { newId } from "../doc/document";
import { bendVerticalEnd, CENTRE, OUTLINE } from "../library/fittings";
import { flangeOf, rubberJointLength } from "../library/flanges";
import { pipeRefOf, rawPipeMeta, rawPorts, type PipeRef, type PipeRefEnd } from "./carry";
import { runColourAt, withLineColour } from "./runColour";

export type PipeEndStyle = "none" | "flat" | "circle" | "flex";

export type PipeAnchor =
  | { kind: "free"; p: Vec }
  /** attached to port `port` of the fitting or pipe group `ownerId` */
  | { kind: "port"; ownerId: string; port: number };

export interface PipeMeta {
  /** nominal size, mm (also the drawn width) */
  size: number;
  a: PipeAnchor;
  b: PipeAnchor;
  endA: PipeEndStyle;
  endB: PipeEndStyle;
}

export interface FittingPortData {
  p: Vec;
  dir: Vec;
  size: number;
  /**
   * 3.15.6: the end points UP, out of the drawing (the outlet of a pump seen from above). A pipe on it runs in any
   * direction on the plan, and its end there is drawn as a floor penetration circle. `dir` is kept for Extend and for
   * turning attached pipes with the owner.
   */
  vertical?: boolean;
}

export interface Port {
  ownerId: string;
  index: number;
  p: Vec;
  /** unit vector pointing OUT of the owner */
  dir: Vec;
  size: number;
  /** true when a pipe is already attached here */
  taken: boolean;
  /** "fitting" or "pipe" */
  ownerKind: "fitting" | "pipe";
  /** short words for the Inspector: "inlet", "outlet", "branch", "large end", "small end", "end A", "end B" */
  label: string;
  /** 3.15.6: the end points up, out of the drawing (see FittingPortData.vertical) */
  vertical?: boolean;
  /** 3.15.6: the end is on a pump (a pipe joined to it gets a rubber joint) */
  pump?: boolean;
}

export type Patch = { id: string; before: Record<string, unknown>; after: Record<string, unknown> };

// ---- Engine notes (2026-10-06) --------------------------------------------------------------
// meta.pipeRef (src/pipes/carry.ts) remembers the port frame each ATTACHED end was built against.
// When a port moves or turns and the pipe's OTHER end is free, the free end swings with the port
// as a rigid body: the pipe keeps its length and its angle to the port (a pipe drawn out along a
// port stays along it). With both ends attached the pipe spans between the two ports.
// Moving a pipe on its own keeps attached ends on their ports: only free ends move.

type Get = (id: string) => Entity | undefined;
type Frame = { p: Vec; dir: Vec; size: number };

const TOL = 1e-6;
const MAX_ROUNDS = 20;
const CIRCLE_POINTS = 48;

const near = (a: number, b: number) => Math.abs(a - b) <= TOL;

/**
 * The collar round a floor penetration (owner, 2026-10-20: "floor penetrations need to show the collar thickness, and
 * the pipe circle stays the same"): mm on each side of the pipe. The same rule as the socket wall of a fitting.
 */
export const collarThickness = (size: number): number => Math.max(3, Math.round(size * 0.08));
const nearVec = (a: Vec, b: Vec) => near(a.x, b.x) && near(a.y, b.y);

/** meta.pipe of a pipe group, or null. */
export function pipeMetaOf(entity: Entity | undefined): PipeMeta | null {
  if (!entity || entity.type !== "group" || (entity.children.length !== 5 && entity.children.length !== 7)) return null;
  return rawPipeMeta(entity);
}

export function isPipe(entity: Entity | undefined): boolean {
  return pipeMetaOf(entity) !== null;
}

/** A fitting group that carries ports. */
export function isFitting(entity: Entity | undefined): boolean {
  return !!entity && entity.type === "group" && !isPipe(entity) && rawPorts(entity) !== null;
}

const FITTING_LABELS: Record<string, string[]> = {
  bend90: ["inlet", "outlet"],
  bend45: ["inlet", "outlet"],
  tee: ["run inlet", "run outlet", "branch"],
  // 3.24.0: the branch of a tee up or down and the outlet of a bend up or down are VERTICAL ends (FittingPortData.vertical)
  teeup: ["run inlet", "run outlet", "branch"],
  teedown: ["run inlet", "run outlet", "branch"],
  // an end cap has its open end only
  bendup: ["inlet", "outlet"],
  benddown: ["inlet", "outlet"],
  cap: ["open end"],
  reducer: ["large end", "small end"],
  // check valve with mounting kit (src/library/checkValves.ts): the flow goes from the inlet to the outlet
  checkvalve: ["inlet", "outlet"],
  // butterfly valve (src/library/butterflyValves.ts): the flow goes either way
  butterfly: ["side A", "side B"],
  // The Davey pumps (src/library/pumps.ts): group meta.pump, ports [inlet, outlet] (a plan has the inlet only).
  pump: ["inlet", "outlet"]
};

/** 3.24.0: a tee or bend whose vertical end drops (its label says "down", not "up"). */
function dropsDown(entity: Entity): boolean {
  const kind = (entity.meta?.fitting as { kind?: unknown } | undefined)?.kind;
  return kind === "teedown" || kind === "benddown";
}

function fittingLabel(entity: Entity, index: number): string {
  // A lint pot (src/library/lintPots.ts): its ends are inlets and outlets, numbered when there is more than one.
  const lp = entity.meta?.lintpot as { ends?: Array<{ role?: unknown }> } | undefined;
  if (Array.isArray(lp?.ends) && typeof lp.ends[index]?.role === "string") {
    const role = lp.ends[index]!.role as string;
    const same = lp.ends.filter((e) => e?.role === role).length;
    return same > 1 ? `${role} ${lp.ends.slice(0, index + 1).filter((e) => e?.role === role).length}` : role;
  }
  // 3.29.0: equipment from a datasheet names its own ends (a chlorinator: inlet, outlet, drain)
  const names = (entity.meta?.equipment as { portNames?: unknown } | undefined)?.portNames;
  if (Array.isArray(names) && typeof names[index] === "string") return names[index] as string;
  const kind = (entity.meta?.fitting as { kind?: unknown } | undefined)?.kind ?? (entity.meta?.pump ? "pump" : undefined);
  return (typeof kind === "string" ? FITTING_LABELS[kind]?.[index] : undefined) ?? `end ${index + 1}`;
}

/** The pipe's children when they are the five (or seven, with the collars) polylines the model needs, else null. */
function pipeChildren(get: Get, g: Entity): PolylineEntity[] | null {
  if (g.type !== "group" || (g.children.length !== 5 && g.children.length !== 7)) return null;
  const out: PolylineEntity[] = [];
  for (const id of g.children) {
    const c = get(id);
    if (!c || c.type !== "polyline") return null;
    out.push(c);
  }
  return out;
}

/** Axis A → B; when the pipe has no length, along the attached port (or +X). */
function axis(a: Vec, b: Vec, fa: Frame | null, fb: Frame | null): Vec {
  const u = normalize({ x: b.x - a.x, y: b.y - a.y });
  if (u.x !== 0 || u.y !== 0) return u;
  return fa ? { ...fa.dir } : fb ? { x: -fb.dir.x, y: -fb.dir.y } : { x: 1, y: 0 };
}

/** Port `index` of `ownerId` as the drawing stands, or null when it does not resolve. */
function resolvePort(get: Get, ownerId: string, index: number, depth = 0): Frame | null {
  if (depth > MAX_ROUNDS) return null; // a pipe chain that loops back on itself
  const owner = get(ownerId);
  if (!owner) return null;
  const pm = pipeMetaOf(owner);
  if (pm) {
    if (index !== 0 && index !== 1) return null;
    const ends = resolveEnds(get, pm, depth + 1);
    if (!ends) return null;
    return index === 0
      ? { p: ends.a, dir: { x: -ends.u.x, y: -ends.u.y }, size: pm.size }
      : { p: ends.b, dir: { ...ends.u }, size: pm.size };
  }
  const pt = rawPorts(owner)?.[index];
  if (!pt) return null;
  const dir = normalize(pt.dir);
  return { p: { ...pt.p }, dir: dir.x === 0 && dir.y === 0 ? { x: 1, y: 0 } : dir, size: pt.size };
}

/** End points of a pipe with meta `pm`, the unit axis A → B and the port frames of attached ends. */
function resolveEnds(get: Get, pm: PipeMeta, depth = 0): { a: Vec; b: Vec; u: Vec; fa: Frame | null; fb: Frame | null } | null {
  const fa = pm.a.kind === "port" ? resolvePort(get, pm.a.ownerId, pm.a.port, depth) : null;
  if (pm.a.kind === "port" && !fa) return null;
  const fb = pm.b.kind === "port" ? resolvePort(get, pm.b.ownerId, pm.b.port, depth) : null;
  if (pm.b.kind === "port" && !fb) return null;
  const a = fa ? fa.p : (pm.a as { p: Vec }).p;
  const b = fb ? fb.p : (pm.b as { p: Vec }).p;
  return { a: { ...a }, b: { ...b }, u: axis(a, b, fa, fb), fa, fb };
}

function takenSet(doc: Doc): Set<string> {
  const out = new Set<string>();
  for (const e of Object.values(doc.entities)) {
    const pm = pipeMetaOf(e);
    if (!pm) continue;
    for (const a of [pm.a, pm.b]) if (a.kind === "port") out.add(`${a.ownerId}#${a.port}`);
  }
  return out;
}

function portsWith(doc: Doc, ownerId: string, taken: Set<string>): Port[] {
  const owner = doc.entities[ownerId];
  if (!owner) return [];
  const get: Get = (id) => doc.entities[id];
  const out: Port[] = [];
  if (isPipe(owner)) {
    for (const index of [0, 1]) {
      const f = resolvePort(get, ownerId, index);
      if (f) out.push({ ownerId, index, p: f.p, dir: f.dir, size: f.size, taken: taken.has(`${ownerId}#${index}`), ownerKind: "pipe", label: index === 0 ? "end A" : "end B" });
    }
    return out;
  }
  if (!isFitting(owner)) return [];
  const isPump = !!owner.meta?.pump;
  rawPorts(owner)!.forEach((raw, index) => {
    const f = resolvePort(get, ownerId, index);
    if (f)
      out.push({
        ownerId, index, p: f.p, dir: f.dir, size: f.size, taken: taken.has(`${ownerId}#${index}`), ownerKind: "fitting",
        label: raw.vertical ? `${fittingLabel(owner, index)} (${dropsDown(owner) ? "down" : "up"})` : fittingLabel(owner, index),
        ...(raw.vertical ? { vertical: true } : {}),
        ...(isPump ? { pump: true } : {})
      });
  });
  return out;
}

/** Ports of one fitting or pipe group (empty for anything else). */
export function portsOf(doc: Doc, ownerId: string): Port[] {
  return portsWith(doc, ownerId, takenSet(doc));
}

/** Every port in the drawing. */
export function allPorts(doc: Doc): Port[] {
  const taken = takenSet(doc);
  const out: Port[] = [];
  for (const e of Object.values(doc.entities)) {
    if (e.type !== "group" || !e.meta) continue;
    if (e.meta.pipe === undefined && e.meta.ports === undefined) continue;
    out.push(...portsWith(doc, e.id, taken));
  }
  return out;
}

/** The nearest port within `tol` mm of `p`, skipping owners in `exclude`; null when none. */
export function portAt(doc: Doc, p: Vec, tol: number, exclude?: Set<string>): Port | null {
  let best: Port | null = null;
  let bestD = Infinity;
  for (const port of allPorts(doc)) {
    if (exclude?.has(port.ownerId)) continue;
    const d = Math.hypot(port.p.x - p.x, port.p.y - p.y);
    if (d <= tol && d < bestD) {
      best = port;
      bestD = d;
    }
  }
  return best;
}

/** The two end points of a pipe as its anchors resolve now (null if an anchor cannot resolve). */
export function pipeEnds(doc: Doc, pipeId: string): { a: Vec; b: Vec } | null {
  const pm = pipeMetaOf(doc.entities[pipeId]);
  if (!pm) return null;
  const ends = resolveEnds((id) => doc.entities[id], pm);
  return ends ? { a: ends.a, b: ends.b } : null;
}

/** Length between the two ends, mm. */
export function pipeLength(doc: Doc, pipeId: string): number {
  const ends = pipeEnds(doc, pipeId);
  return ends ? Math.hypot(ends.b.x - ends.a.x, ends.b.y - ends.a.y) : 0;
}

// ---- geometry ----

interface ChildGeom {
  points: Vec[];
  closed: boolean;
  hidden: boolean;
}

function endGeom(p: Vec, n: Vec, h: number, style: PipeEndStyle): ChildGeom {
  if (style === "circle") {
    const points: Vec[] = [];
    for (let i = 0; i < CIRCLE_POINTS; i++) {
      const t = (2 * Math.PI * i) / CIRCLE_POINTS;
      points.push({ x: p.x + h * Math.cos(t), y: p.y + h * Math.sin(t) });
    }
    return { points, closed: true, hidden: false };
  }
  // "flat" and "none" share the line across the end; "none" hides it (2 valid points kept, INV-4)
  return {
    points: [
      { x: p.x + n.x * h, y: p.y + n.y * h },
      { x: p.x - n.x * h, y: p.y - n.y * h }
    ],
    closed: false,
    hidden: style === "none"
  };
}

/** A collar circle at p, of radius r: shown only on a floor penetration end. */
function collarGeom(p: Vec, r: number, style: PipeEndStyle): ChildGeom {
  const points: Vec[] = [];
  for (let i = 0; i < CIRCLE_POINTS; i++) {
    const t = (2 * Math.PI * i) / CIRCLE_POINTS;
    points.push({ x: p.x + r * Math.cos(t), y: p.y + r * Math.sin(t) });
  }
  return { points, closed: true, hidden: style !== "circle" };
}

/** Points along one half of the rubber sphere. */
const FLEX_STEPS = 16;

/**
 * The outline of a rubber joint (3.15.6) at end point p, running into the pipe along unit `ui`, `len` long: the flange
 * on the end (PN16 outside diameter and thickness), the rubber sphere (it swells out between the flanges), the flange on
 * the pipe side. One closed line. The inner faces of the flanges are open where the sphere meets them.
 */
export function rubberJointOutline(p: Vec, ui: Vec, size: number, len: number): Vec[] {
  const f = flangeOf(size);
  const Rf = f.od / 2;
  const t = Math.min(f.t, len / 4);
  const half = size / 2;
  const r0 = half + Math.max(3, 0.12 * size); // the rubber wall at the flanges
  const hb = Math.min(Rf - 4, half + 0.6 * (Rf - half)); // the top of the sphere
  const n = { x: -ui.y, y: ui.x };
  const at = (u: number, v: number): Vec => ({ x: p.x + ui.x * u + n.x * v, y: p.y + ui.y * u + n.y * v });
  const bulge = (k: number, side: number): Vec => {
    const s = k / FLEX_STEPS;
    return at(t + s * (len - 2 * t), side * (r0 + (hb - r0) * Math.sin(Math.PI * s)));
  };
  const out: Vec[] = [at(0, -Rf), at(t, -Rf), at(t, -r0)];
  for (let k = 1; k < FLEX_STEPS; k++) out.push(bulge(k, -1));
  out.push(at(len - t, -r0), at(len - t, -Rf), at(len, -Rf), at(len, Rf), at(len - t, Rf), at(len - t, r0));
  for (let k = FLEX_STEPS - 1; k >= 1; k--) out.push(bulge(k, 1));
  out.push(at(t, r0), at(t, Rf), at(0, Rf));
  return out;
}

/** The length of the rubber joint on an end of a pipe `total` long: the joint's face to face length, never more than 45 % of the pipe. */
const jointLen = (size: number, total: number): number => Math.max(0, Math.min(rubberJointLength(size), 0.45 * total));

/** The seven children [side 1, side 2, centre, end A, end B, collar A, collar B] for a pipe from a to b. */
function pipeGeometry(a: Vec, b: Vec, u: Vec, size: number, endA: PipeEndStyle, endB: PipeEndStyle): ChildGeom[] {
  const h = Math.max(0, size) / 2;
  const n = { x: -u.y, y: u.x };
  const off = (p: Vec, s: number): Vec => ({ x: p.x + n.x * h * s, y: p.y + n.y * h * s });
  const rc = h + collarThickness(size);
  const total = Math.hypot(b.x - a.x, b.y - a.y);
  // a rubber joint end: the sides start at its far flange
  const la = endA === "flex" ? jointLen(size, total) : 0;
  const lb = endB === "flex" ? jointLen(size, total) : 0;
  const sa = { x: a.x + u.x * la, y: a.y + u.y * la };
  const sb = { x: b.x - u.x * lb, y: b.y - u.y * lb };
  const end = (p: Vec, style: PipeEndStyle, ui: Vec, l: number): ChildGeom =>
    style === "flex" && l > 0 ? { points: rubberJointOutline(p, ui, size, l), closed: true, hidden: false } : endGeom(p, n, h, style === "flex" ? "flat" : style);
  return [
    { points: [off(sa, 1), off(sb, 1)], closed: false, hidden: false },
    { points: [off(sa, -1), off(sb, -1)], closed: false, hidden: false },
    { points: [{ ...a }, { ...b }], closed: false, hidden: false },
    end(a, endA, u, la),
    end(b, endB, { x: -u.x, y: -u.y }, lb),
    collarGeom(a, rc, endA),
    collarGeom(b, rc, endB)
  ];
}

/**
 * The look of a pipe end that joins port `port` (3.15.6): a floor penetration circle on a vertical end (the top of a
 * pump on a plan), a rubber joint on any other end of a pump, else joined (no line across the end).
 */
export function joinedEndStyle(port: Pick<Port, "vertical" | "pump"> | null | undefined): PipeEndStyle {
  if (port?.vertical) return "circle";
  if (port?.pump) return "flex";
  return "none";
}

function samePoints(p: Vec[], q: Vec[]): boolean {
  return p.length === q.length && p.every((v, i) => nearVec(v, q[i]!));
}

function sameAnchor(a: PipeAnchor, b: PipeAnchor): boolean {
  if (a.kind === "free" && b.kind === "free") return nearVec(a.p, b.p);
  if (a.kind === "port" && b.kind === "port") return a.ownerId === b.ownerId && a.port === b.port;
  return false;
}

function samePipeMeta(a: PipeMeta, b: PipeMeta): boolean {
  return near(a.size, b.size) && a.endA === b.endA && a.endB === b.endB && sameAnchor(a.a, b.a) && sameAnchor(a.b, b.b);
}

function sameRefEnd(a: PipeRefEnd | undefined, b: PipeRefEnd | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.ownerId === b.ownerId && a.port === b.port && nearVec(a.p, b.p) && nearVec(a.dir, b.dir);
}

/** Turn `v` by the turn that takes unit `d0` to unit `d1`. */
function turnBy(v: Vec, d0: Vec, d1: Vec): Vec {
  const c = d0.x * d1.x + d0.y * d1.y;
  const s = d0.x * d1.y - d0.y * d1.x;
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

function refEnd(anchor: PipeAnchor, f: Frame | null): PipeRefEnd | undefined {
  return anchor.kind === "port" && f ? { ownerId: anchor.ownerId, port: anchor.port, p: { ...f.p }, dir: { ...f.dir } } : undefined;
}

/**
 * One pipe brought in line with its anchors: broken anchors freed (end cut flat), a free end
 * swung with the port the other end is on, children rebuilt. Returns the changed entities
 * (copies), or null when the pipe is already right.
 */
function rebuildOne(get: Get, g: Entity): Entity[] | null {
  const pm = pipeMetaOf(g);
  if (!pm || g.type !== "group") return null;
  const kids = pipeChildren(get, g);
  if (!kids) return null;
  const centre = kids[2]!.points;
  const next: PipeMeta = structuredClone(pm);
  const ref = pipeRefOf(g);

  // 1. an end whose owner (or port) is gone: free it where it is drawn now, cut flat
  for (const k of ["a", "b"] as const) {
    const anchor = next[k];
    if (anchor.kind !== "port" || resolvePort(get, anchor.ownerId, anchor.port)) continue;
    const at = k === "a" ? centre[0] : centre[centre.length - 1];
    if (!at) return null;
    next[k] = { kind: "free", p: { x: at.x, y: at.y } };
    if (k === "a") next.endA = "flat";
    else next.endB = "flat";
  }

  // 2. a free end swings with the port its other end is on (rigid: length and angle kept)
  const frameOf = (anchor: PipeAnchor) => (anchor.kind === "port" ? resolvePort(get, anchor.ownerId, anchor.port) : null);
  const fa = frameOf(next.a);
  const fb = frameOf(next.b);
  const swing = (attached: PipeAnchor, frame: Frame | null, r: PipeRefEnd | undefined, free: PipeAnchor): PipeAnchor => {
    if (attached.kind !== "port" || free.kind !== "free" || !frame || !r) return free;
    if (r.ownerId !== attached.ownerId || r.port !== attached.port) return free;
    if (nearVec(r.p, frame.p) && nearVec(r.dir, frame.dir)) return free;
    const v = turnBy({ x: free.p.x - r.p.x, y: free.p.y - r.p.y }, r.dir, frame.dir);
    return { kind: "free", p: { x: frame.p.x + v.x, y: frame.p.y + v.y } };
  };
  next.b = swing(next.a, fa, ref.a, next.b);
  next.a = swing(next.b, fb, ref.b, next.a);

  // 3. remember the port frames this build used
  const nextRef: PipeRef = {};
  const ra = refEnd(next.a, fa);
  const rb = refEnd(next.b, fb);
  if (ra) nextRef.a = ra;
  if (rb) nextRef.b = rb;

  // 4. geometry
  const a = fa ? fa.p : (next.a as { p: Vec }).p;
  const b = fb ? fb.p : (next.b as { p: Vec }).p;
  const geom = pipeGeometry(a, b, axis(a, b, fa, fb), next.size, next.endA, next.endB);

  const out: Entity[] = [];
  if (!(samePipeMeta(pm, next) && sameRefEnd(ref.a, nextRef.a) && sameRefEnd(ref.b, nextRef.b))) {
    const meta: Record<string, unknown> = { ...(g.meta ?? {}), pipe: next };
    if (nextRef.a || nextRef.b) meta.pipeRef = nextRef;
    else delete meta.pipeRef;
    out.push({ ...g, meta });
  }
  kids.forEach((kid, i) => {
    const want = geom[i]!;
    const hideManaged = i >= 3; // end and collar children show or hide by style; sides and centre keep their flag
    const same = samePoints(kid.points, want.points) && kid.closed === want.closed && (!hideManaged || kid.hidden === want.hidden);
    if (!same) out.push({ ...kid, points: want.points, closed: want.closed, hidden: hideManaged ? want.hidden : kid.hidden });
  });
  return out.length > 0 ? out : null;
}

/** Fields a rebuild can change. */
const PATCH_KEYS = ["meta", "points", "closed", "hidden"] as const;

function rebuildWith(doc: Doc, overlay: Map<string, Entity>): Patch[] {
  const get: Get = (id) => overlay.get(id) ?? doc.entities[id];
  const pipeIds: string[] = [];
  for (const e of Object.values(doc.entities)) if (pipeMetaOf(e)) pipeIds.push(e.id);
  for (let round = 0; round < MAX_ROUNDS; round++) {
    let changed = false;
    for (const id of pipeIds) {
      const g = get(id);
      const made = g ? rebuildOne(get, g) : null;
      if (!made) continue;
      for (const e of made) overlay.set(e.id, e);
      changed = true;
    }
    if (!changed) break;
  }
  const patches: Patch[] = [];
  for (const [id, e] of overlay) {
    const orig = doc.entities[id];
    if (!orig) continue;
    const before: Record<string, unknown> = {};
    const after: Record<string, unknown> = {};
    for (const k of PATCH_KEYS) {
      const o = (orig as unknown as Record<string, unknown>)[k];
      const n = (e as unknown as Record<string, unknown>)[k];
      if (JSON.stringify(o) === JSON.stringify(n)) continue;
      before[k] = structuredClone(o);
      after[k] = structuredClone(n);
    }
    if (Object.keys(after).length > 0) patches.push({ id, before, after });
  }
  return patches;
}

// ---- commands ----

function polyline(id: string, layerId: string, geom: ChildGeom, style: Style): PolylineEntity {
  return { id, type: "polyline", layerId, locked: false, hidden: geom.hidden, points: geom.points, closed: geom.closed, style: structuredClone(style) };
}

/** New pipe (5 polylines + its group) on `layerId` as ONE undoable command. Throws when a port anchor does not resolve. */
export function makePipeCommand(doc: Doc, layerId: string, meta: PipeMeta): { cmd: Command; groupId: string } {
  const ends = resolveEnds((id) => doc.entities[id], meta);
  if (!ends) throw new Error("pipes: a pipe end is attached to a port that does not exist");
  const geom = pipeGeometry(ends.a, ends.b, ends.u, meta.size, meta.endA, meta.endB);
  // 3.18.0: a pipe on the end of a coloured pipe or fitting takes its colour (runColour.ts)
  const colour = runColourAt(doc, [meta.a, meta.b]);
  const styles = [OUTLINE, OUTLINE, CENTRE, OUTLINE, OUTLINE, OUTLINE, OUTLINE].map((s) => withLineColour(s, colour));
  const kids = geom.map((g, i) => polyline(newId(), layerId, g, styles[i]!));
  const ref: PipeRef = {};
  const ra = refEnd(meta.a, ends.fa);
  const rb = refEnd(meta.b, ends.fb);
  if (ra) ref.a = ra;
  if (rb) ref.b = rb;
  const groupMeta: Record<string, unknown> = { pipe: structuredClone(meta) };
  if (ra || rb) groupMeta.pipeRef = ref;
  const group: GroupEntity = { id: newId(), type: "group", layerId, locked: false, hidden: false, children: kids.map((k) => k.id), meta: groupMeta };
  const cmd: Command = {
    kind: "batch",
    label: "add pipe",
    commands: [
      { kind: "addEntities", entities: kids, z: kids.map(() => ({ layerId, index: -1 })) },
      { kind: "group", group, z: { layerId, index: -1 }, children: kids.map((k) => ({ id: k.id, z: { layerId, index: -1 } })) }
    ]
  };
  return { cmd, groupId: group.id };
}

/**
 * A pipe out of port `port` of `ownerId`, `length` mm long along the port's direction, the size of
 * the port. End A is attached (style "none"); end B is free with `endStyle` (default "flat").
 * Null when the port does not exist or the length is not a number above 0.
 */
export function extendFromPortCommand(
  doc: Doc,
  layerId: string,
  ownerId: string,
  port: number,
  length: number,
  endStyle?: PipeEndStyle
): { cmd: Command; groupId: string } | null {
  if (!Number.isFinite(length) || length <= 0) return null;
  const f = resolvePort((id) => doc.entities[id], ownerId, port);
  if (!f) return null;
  const pt = portsOf(doc, ownerId).find((x) => x.index === port);
  return makePipeCommand(doc, layerId, {
    size: f.size,
    a: { kind: "port", ownerId, port },
    b: { kind: "free", p: { x: f.p.x + f.dir.x * length, y: f.p.y + f.dir.y * length } },
    endA: joinedEndStyle(pt),
    endB: endStyle ?? "flat"
  });
}

/**
 * Change a pipe. `length` moves the FREE end along the pipe (end B if free, else end A; nothing
 * when both ends are attached). Other fields replace meta.pipe fields (e.g. size, endA, a: anchor).
 * Returns patches for the pipe and its children (rebuilt), plus any pipes chained to it.
 * A length below 0 or not a number is ignored; with both ends attached and only `length` given
 * the result is [] (other fields given with it still apply). A size not above 0 is ignored.
 */
export function setPipePatches(doc: Doc, pipeId: string, change: Partial<PipeMeta> & { length?: number }): Patch[] {
  const g = doc.entities[pipeId];
  const pm = pipeMetaOf(g);
  if (!g || !pm) return [];
  const { length, ...fields } = change;
  const next: PipeMeta = structuredClone(pm);
  if (fields.size !== undefined && Number.isFinite(fields.size) && fields.size > 0) next.size = fields.size;
  if (fields.a) next.a = structuredClone(fields.a);
  if (fields.b) next.b = structuredClone(fields.b);
  if (fields.endA) next.endA = fields.endA;
  if (fields.endB) next.endB = fields.endB;
  if (length !== undefined) {
    const bothAttached = next.a.kind === "port" && next.b.kind === "port";
    if (bothAttached && Object.keys(fields).length === 0) return [];
    if (!bothAttached && Number.isFinite(length) && length >= 0) {
      const ends = resolveEnds((id) => doc.entities[id], next);
      if (!ends) return [];
      if (next.b.kind === "free") next.b = { kind: "free", p: { x: ends.a.x + ends.u.x * length, y: ends.a.y + ends.u.y * length } };
      else next.a = { kind: "free", p: { x: ends.b.x - ends.u.x * length, y: ends.b.y - ends.u.y * length } };
    }
  }
  const overlay = new Map<string, Entity>();
  overlay.set(pipeId, { ...g, meta: { ...(g.meta ?? {}), pipe: next } });
  return rebuildWith(doc, overlay);
}

/**
 * Load upgrade (3.15.2): a pipe with five children gets its two collar children (hidden unless the end is a floor
 * penetration), in place. Returns how many pipes got them.
 */
export function addPipeCollarsInPlace(doc: Doc): number {
  const get: Get = (id) => doc.entities[id];
  let n = 0;
  for (const g of Object.values(doc.entities)) {
    const pm = pipeMetaOf(g);
    if (!pm || g.type !== "group" || g.children.length !== 5) continue;
    const kids = pipeChildren(get, g);
    const ends = resolveEnds(get, pm);
    if (!kids || !ends) continue;
    const geom = pipeGeometry(ends.a, ends.b, ends.u, pm.size, pm.endA, pm.endB);
    const style = kids[3]!.style;
    const made = [geom[5]!, geom[6]!].map((cg) => polyline(newId(), kids[3]!.layerId, cg, style));
    for (const e of made) doc.entities[e.id] = e;
    g.children = [...g.children, ...made.map((e) => e.id)];
    n++;
  }
  return n;
}

/**
 * Load upgrade (3.24.0), in place: a tee up or down placed before 3.24.0 has its two run ends only, and a bend up or
 * down its inlet only. Each gets its VERTICAL end at the centre of its circle (the branch of a tee, half way between
 * its run ends; the outlet of a bend, at the end of its leg), so a pipe can start there. Returns how many changed.
 */
export function addVerticalFittingEndsInPlace(doc: Doc): number {
  let n = 0;
  for (const g of Object.values(doc.entities)) {
    if (g.type !== "group") continue;
    const f = g.meta?.fitting as { kind?: unknown; size?: unknown; size2?: unknown } | undefined;
    const ports = rawPorts(g);
    if (!f || !ports || typeof f.size !== "number") continue;
    let end: FittingPortData | null = null;
    if ((f.kind === "teeup" || f.kind === "teedown") && ports.length === 2) {
      const [a, b] = ports as [FittingPortData, FittingPortData];
      const run = normalize({ x: b.p.x - a.p.x, y: b.p.y - a.p.y });
      const branch = typeof f.size2 === "number" ? f.size2 : f.size;
      end = { p: { x: (a.p.x + b.p.x) / 2, y: (a.p.y + b.p.y) / 2 }, dir: { x: run.y, y: -run.x }, size: branch, vertical: true };
    } else if ((f.kind === "bendup" || f.kind === "benddown") && ports.length === 1) {
      end = bendVerticalEnd(ports[0]!);
    }
    if (!end) continue;
    g.meta = { ...(g.meta ?? {}), ports: [...ports, end] };
    n++;
  }
  return n;
}

/** Patches that bring every pipe's children in line with its anchors (cascades along chains). */
export function rebuildPatches(doc: Doc): Patch[] {
  return rebuildWith(doc, new Map());
}

/** Commands that cannot move a port or a pipe end. */
const NO_GEOMETRY = new Set<Command["kind"]>(["setDocProps", "addAsset", "removeAsset", "updateLayer", "reorderLayer", "addLayer", "deleteLayer"]);

/**
 * For DocStore.deriver: after a command is applied, the follow-up command that rebuilds the
 * pipes it disturbed (and frees anchors whose owner was deleted), or null when nothing to do.
 * The store records [cmd, follow-up] as ONE undo step.
 */
export function pipeDeriver(doc: Doc, cmd: Command): Command | null {
  if (NO_GEOMETRY.has(cmd.kind)) return null;
  let any = false;
  for (const e of Object.values(doc.entities)) {
    if (e.type === "group" && e.meta?.pipe !== undefined) {
      any = true;
      break;
    }
  }
  if (!any) return null;
  const patches = rebuildPatches(doc);
  return patches.length > 0 ? { kind: "updateEntities", patches } : null;
}
